/* eslint-disable camelcase */
import { call, put, take, race, select, takeEvery, cancelled } from 'redux-saga/effects'
import axios from 'axios'
import { normalize } from 'normalizr'
import { get, last } from 'lodash-es'

import API from '../config'
import { selectUser } from './user'
import { getReferrerId } from './referrer'
import { receiveSearch } from './search'
import { card, comment } from '../schemas'
import { getHeaders } from './api'

const rootApi = `${API.apiPath}/${API.apiVersion}/`

export const REQUEST_COMMENTS = 'REQUEST_COMMENTS'
export const RECEIVE_COMMENTS = 'RECEIVE_COMMENTS'
export const REQUEST_ADD_COMMENT = 'REQUEST_ADD_COMMENT'
export const COMMENT_ADDED = 'COMMENT_ADDED'
export const REQUEST_LIKE_COMMENT = 'REQUEST_LIKE_COMMENT'
export const RECEIVE_LIKED_COMMENT = 'RECEIVE_LIKED_COMMENT'
const REQUEST_UPDATE_COMMENT = 'REQUEST_UPDATE_COMMENT'
export const COMMENT_UPDATED = 'COMMENT_UPDATED'
const REQUEST_TOP_COMMENTS = 'REQUEST_TOP_COMMENTS'
export const RECEIVE_TOP_COMMENTS = 'RECEIVE_TOP_COMMENTS'

const defaultLimit = 21

export const commentType = {
  latest: 'latest_comment',
  referrer: 'referrer_comment',
  owner: 'first_owner_comment',
  my: 'my_comment'
}

/** *************** Schemas ****************************/
/** ***************************************************/

export function requestComments(id) {
  return {
    type: REQUEST_COMMENTS,
    payload: { id }
  }
}

export function requestAddComment(card_id, comment_content) {
  return {
    type: REQUEST_ADD_COMMENT,
    payload: { card_id, comment_content }
  }
}

function commentAdded(data, referrerId) {
  return {
    type: COMMENT_ADDED,
    payload: { data, referrerId }
  }
}

export function requestLikeComment(comment_id, unlike = false) {
  return {
    type: REQUEST_LIKE_COMMENT,
    payload: { comment_id, unlike }
  }
}

export function receiveComments(data) {
  return {
    type: RECEIVE_COMMENTS,
    payload: { data }
  }
}

export function receiveLikedComment(id, unlike = false) {
  return {
    type: RECEIVE_LIKED_COMMENT,
    payload: { id, unlike }
  }
}

export function requestUpdateComment(payload) {
  return {
    type: REQUEST_UPDATE_COMMENT,
    payload
  }
}

function commentUpdated(payload) {
  return {
    type: COMMENT_UPDATED,
    payload
  }
}

export function requestTopComments(payload) {
  return {
    type: REQUEST_TOP_COMMENTS,
    payload
  }
}

export function receiveTopComments(payload) {
  return {
    type: RECEIVE_TOP_COMMENTS,
    payload
  }
}

export function* getComments(action) {
  const url = rootApi + `deck/get_comments`
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const CancelToken = axios.CancelToken
  const source = CancelToken.source()
  const { id } = action.payload

  const headers = getHeaders(token)

  const params = { card_id: id }

  try {
    const response = yield call([axios, 'get'], url, {
      params,
      headers,
      cancelToken: source.token
    })

    if (response.status && response.status === 200) {
      const { comments } = response.data
      const originalData = { id, comments }
      const normalizedData = normalize(originalData, card)
      yield put(receiveComments(normalizedData))
    }
  } catch (e) {
  } finally {
    if (yield cancelled()) {
      source.cancel()
    }
  }
}

function* addComment(action) {
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const { card_id, comment_content } = action.payload
  const response = yield call(
    [axios, 'post'],
    'deck/add_comment',
    {
      card_id,
      comment_content
    },
    {
      baseURL: `${API.apiPath}/${API.apiVersion}/`,
      headers: getHeaders(token)
    }
  )

  if (response.status && response.status === 200) {
    const { comment } = response.data
    const originalData = { id: card_id, comments: [comment] }
    const normalizedData = normalize(originalData, card)
    const referrerId = yield select(getReferrerId)
    yield put(commentAdded(normalizedData, referrerId))
  }
}

function* likeComment(action) {
  const { comment_id, unlike } = action.payload
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')

  yield put(receiveLikedComment(comment_id, unlike))

  try {
    yield call(
      [axios, 'post'],
      `deck/${unlike ? 'unlike_comment' : 'like_comment'}`,
      {
        comment_id
      },
      {
        baseURL: `${API.apiPath}/${API.apiVersion}/`,
        headers: getHeaders(token)
      }
    )
  } catch (e) {
    yield put(receiveLikedComment(comment_id, !unlike))
  }
}

function* editComment(action) {
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const { id, content } = action.payload
  const response = yield call(
    [axios, 'post'],
    'deck/update_comment',
    {
      id,
      content
    },
    {
      baseURL: `${API.apiPath}/${API.apiVersion}/`,
      headers: getHeaders(token)
    }
  )

  if (response.status && response.status === 200) {
    yield put(commentUpdated({ id, content }))
  }
}

function* getTopComments(action) {
  const url = rootApi + `deck/top-comments`
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const { maxId } = action.payload
  const CancelToken = axios.CancelToken
  const source = CancelToken.source()
  const headers = getHeaders(token)
  const params = { max_id: maxId, limit: defaultLimit }

  try {
    const response = yield call([axios, 'get'], url, {
      params,
      headers,
      cancelToken: source.token
    })

    if (response.status && response.status === 200) {
      const normalizedData = normalize(response.data.comments, [comment])
      yield put(
        receiveSearch({
          queryObject: { type: 'topComments' },
          end: response.data.comments.length < defaultLimit,
          data: response.data.comments.map(i => i.card.id),
          maxId: last(response.data.comments).id
        })
      )
      yield put(receiveTopComments({ data: normalizedData }))
    }
  } catch (e) {
  } finally {
    if (yield cancelled()) {
      source.cancel()
    }
  }
}

function* getCommentsSaga() {
  while (true) {
    const action = yield take(REQUEST_COMMENTS)
    yield race({
      task: call(getComments, action),
      cancel: take('LOCATION_CHANGE')
    })
  }
}

function* addCommentsSaga() {
  yield takeEvery(REQUEST_ADD_COMMENT, addComment)
}

function* likeCommentSaga() {
  yield takeEvery(REQUEST_LIKE_COMMENT, likeComment)
}

function* editCommentSaga() {
  yield takeEvery(REQUEST_UPDATE_COMMENT, editComment)
}

function* getTopCommentsSaga() {
  while (true) {
    const action = yield take(REQUEST_TOP_COMMENTS)
    yield race({
      task: call(getTopComments, action),
      cancel: take('LOCATION_CHANGE')
    })
  }
}

export const sagas = [likeCommentSaga, getCommentsSaga, addCommentsSaga, editCommentSaga, getTopCommentsSaga]
